<template>
  <div v-if="pagesCount > 1" class="pagination">
    <div class="pagination__buttons">
      <IconButton
        value="arrow-roll-up"
        :disabled="Boolean(pageToShow === 1)"
        @click="_handlePagination('first')"
      />
      <IconButton
        value="arrow-tiny-short-left"
        :disabled="Boolean(pageToShow === 1)"
        @click="_handlePagination(-1)"
      />
      <div class="pagination__page-size-label">
        <Input
          :value="pageInput"
          :ui="{ input: 'pagination__input' }"
          placeholder=""
          @input="onInput"
          @on-enter="_handleEnterKeydown"
          @focusout="onFocusout"
        />
        <span class="noselect">/</span>
        <span> {{ pagesCount }} </span>
      </div>
      <IconButton
        value="arrow-tiny-short-right"
        :disabled="Boolean(pageToShow === pagesCount)"
        @click="_handlePagination(1)"
      />
      <IconButton
        value="arrow-reveal"
        :disabled="Boolean(pageToShow === pagesCount)"
        @click="_handlePagination('last')"
      />
    </div>
  </div>
</template>

<script setup>
import { IconButton, Input } from '~packages/epk-front-shared/ui';
</script>

<script>
export default {
  name: 'EpkPagination',
  props: {
    meta: { type: Object, require: true, default: () => ({}) },
  },
  emits: [
    'changeSize',
    'clickAll',
    'clickNext',
    'clickPage',
    'clickPrevious',
    'onEnter',
  ],
  data() {
    return {
      pageInput: this.$route.query.page ? +this.$route.query.page + 1 : 1,
      page: +this.$route.query.page,
      size: +this.$route.query.size,
    };
  },
  computed: {
    pagesCount() {
      if (this.meta.totalPages) return this.meta.totalPages;
      else return 1;
    },
    pageToShow() {
      return this.page + 1;
    },
  },
  watch: {
    size: function (newVal, oldValue) {
      if (newVal === oldValue) return;
      if (newVal) {
        this.page = 0;
        this.setQuery(newVal);
        this.$emit('changeSize', newVal);
      }
    },
    meta: {
      deep: true,
      immediate: true,
      handler(value) {
        if (!value?.use_pagination) return;

        (this.page = value.pagination
          ? value.pagination.page > 0
            ? value.pagination.page
            : 0
          : 0),
          (this.size = value.pagination ? value.pagination.size : this.size);
        this.setQuery();
      },
    },
    $route: function (f) {
      if (
        !isNaN(Number(f.query.page)) &&
        Number(f.query.page) + 1 !== this.pageInput
      )
        this.pageInput = Number(f.query.page) + 1;
    },
  },
  mounted() {
    const params = this.$route.query;
    if (Object.keys(params).length > 0) {
      if (params.page) {
        this.page = parseInt(params.page);
      }
    }
    this.size ||= 20;
  },
  methods: {
    _handleClickPage(page) {
      this.page = page;
      this.setQuery();
      this.$emit('clickPage', this.page, this.size);
    },
    _handleEnterKeydown(event) {
      const value = Math.min(this.pagesCount, Math.max(event.target.value, 1));
      this.page = value - 1;
      this.setQuery();
      this.$emit('clickPage', value - 1, this.size);
    },
    setQuery() {
      const query = Object.assign({}, this.$route.query);
      query.page = this.page;
      query.size = this.size;
      this.$router.replace({ query });
    },
    onInput(v) {
      const vInt = v ? parseInt(v) : v;
      if (isNaN(vInt) || vInt > this.pagesCount || vInt < 0) return;
      this.pageInput = +vInt;
    },
    onFocusout(event) {
      this.pageInput = this.pageInput ? this.pageInput : 1;
      this._handleEnterKeydown(event);
    },
    _handlePagination(e) {
      if (typeof e === 'number' || e === 'first' || e === 'last') {
        if (this.pageInput + e > this.pagesCount || this.pageInput + e <= 0)
          return;

        switch (e) {
          case 'last':
            this.page = this.pagesCount - 1;
            this.pageInput = this.pagesCount;
            break;
          case 'first':
            this.page = 0;
            this.pageInput = 1;
            break;
          default:
            this.page += +e;
            this.pageInput = this.page + 1;
            break;
        }

        this.setQuery();
        this.$emit('clickPage', this.page, this.size);
      }
    },
  },
};
</script>

<style src="./EpkPagination.scss" lang="scss" />
